import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@foundation/components/ui/button';
import { Label } from '@foundation/components/ui/label';

export type QuestionFormData = {
  text: string;
  input_type: string;
  options: string[];
  placeholder: string;
  used_for: string;
  is_required: boolean;
};

type QuestionFormProps = {
  initial?: Partial<QuestionFormData>;
  onSave: (data: QuestionFormData) => Promise<void>;
  onCancel: () => void;
};

const INPUT_TYPE_OPTIONS = [
  { value: 'yes-no', label: 'Yes / No' },
  { value: 'yes-no-not-sure', label: 'Yes / No / Not Sure' },
  { value: 'single-select', label: 'Single Select (dropdown)' },
  { value: 'multi-select', label: 'Multi Select (checkboxes)' },
  { value: 'text', label: 'Text (single line)' },
  { value: 'textarea', label: 'Textarea (multi-line)' },
];

const INPUT_CLASS =
  'w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#33b864]/30 focus:border-[#33b864]/40 transition-all duration-200';

export const QuestionForm = ({ initial, onSave, onCancel }: QuestionFormProps) => {
  const [text, setText] = useState(initial?.text ?? '');
  const [inputType, setInputType] = useState(initial?.input_type ?? 'yes-no');
  const [options, setOptions] = useState<string[]>(initial?.options ?? []);
  const [newOption, setNewOption] = useState('');
  const [placeholder, setPlaceholder] = useState(initial?.placeholder ?? '');
  const [usedFor, setUsedFor] = useState(initial?.used_for ?? '');
  const [isRequired, setIsRequired] = useState(initial?.is_required !== false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsOptions = inputType === 'single-select' || inputType === 'multi-select';

  const addOption = () => {
    const trimmed = newOption.trim();
    if (trimmed && !options.includes(trimmed)) {
      setOptions([...options, trimmed]);
    }
    setNewOption('');
  };

  const removeOption = (opt: string) => setOptions(options.filter((o) => o !== opt));

  const handleSave = async () => {
    if (!text.trim()) { setError('Question text is required.'); return; }
    if (needsOptions && options.length === 0) { setError('At least one option is required.'); return; }
    setError(null);
    setIsSaving(true);
    try {
      await onSave({
        text: text.trim(),
        input_type: inputType,
        options: needsOptions ? options : [],
        placeholder: placeholder.trim(),
        used_for: usedFor.trim(),
        is_required: isRequired,
      });
    } catch {
      setError('Failed to save question. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Question text */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-foreground">Question Text *</Label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder="e.g. Does the organisation operate in multiple states?"
          className={`${INPUT_CLASS} resize-none`}
        />
      </div>

      {/* Input type */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-foreground">Input Type *</Label>
        <select
          value={inputType}
          onChange={(e) => setInputType(e.target.value)}
          className={INPUT_CLASS}
        >
          {INPUT_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Options (single/multi-select only) */}
      {needsOptions && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-foreground">Options *</Label>
          <div className="space-y-2">
            {options.map((opt) => (
              <div key={opt} className="flex items-center gap-2">
                <span className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-muted/40 text-sm">{opt}</span>
                <button
                  type="button"
                  onClick={() => removeOption(opt)}
                  className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addOption()}
              placeholder="Add option…"
              className={INPUT_CLASS}
            />
            <Button type="button" variant="outline" size="sm" onClick={addOption} className="flex-shrink-0">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Placeholder (text/textarea) */}
      {(inputType === 'text' || inputType === 'textarea') && (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-foreground">Placeholder</Label>
          <input
            type="text"
            value={placeholder}
            onChange={(e) => setPlaceholder(e.target.value)}
            className={INPUT_CLASS}
            placeholder="Optional placeholder text…"
          />
        </div>
      )}

      {/* Used for */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-foreground">Used For (tooltip)</Label>
        <input
          type="text"
          value={usedFor}
          onChange={(e) => setUsedFor(e.target.value)}
          className={INPUT_CLASS}
          placeholder="e.g. Determines multi-jurisdiction policy requirements"
        />
      </div>

      {/* Required toggle */}
      <div className="flex items-center gap-3">
        <Label className="text-sm font-medium text-foreground">Required</Label>
        <div className="flex gap-2">
          {[true, false].map((val) => (
            <button
              key={String(val)}
              type="button"
              onClick={() => setIsRequired(val)}
              className={[
                'px-4 py-1.5 text-sm font-medium rounded-lg border transition-all',
                isRequired === val ? 'bg-[#33b864] text-white border-[#33b864]' : 'bg-background border-border',
              ].join(' ')}
            >
              {val ? 'Yes' : 'No'}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button
          type="button"
          disabled={isSaving}
          onClick={handleSave}
          className="bg-[#33b864] hover:bg-[#2da358] text-white border-0"
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};
